import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    ArrowLeft,
    Clock,
    Filter,
    Download,
    Calendar,
    Users,
    Search,
    Shield,
    FileSpreadsheet
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TimecardPage() {
    const navigate = useNavigate();
    const { user, hasUnitAccess, hasGroupAccess } = useAuth();
    const [attendance, setAttendance] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedGroup, setSelectedGroup] = useState('all');
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [search, setSearch] = useState('');

    // Check if user can only see their group
    const isGroupLevelOnly = () => {
        const groupOnlyRoles = ['group_leader', 'deputy_group_leader', 'group_administrator'];
        return user && groupOnlyRoles.includes(user.role);
    };

    useEffect(() => {
        fetchData();
    }, [selectedGroup, selectedMonth]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Build query params
            const params = new URLSearchParams();
            
            // For group-level users, force filter to their group
            if (isGroupLevelOnly() && user?.group_id) {
                params.append('group_id', user.group_id);
            } else if (selectedGroup !== 'all') {
                params.append('group_id', selectedGroup);
            }
            
            // Month filter
            if (selectedMonth) {
                const [year, month] = selectedMonth.split('-');
                const startDate = new Date(year, month - 1, 1);
                const endDate = new Date(year, month, 0, 23, 59, 59);
                params.append('start_date', startDate.toISOString());
                params.append('end_date', endDate.toISOString());
            }

            const [attendanceRes, groupsRes] = await Promise.all([
                axios.get(`${API_URL}/attendance/all?${params.toString()}`),
                axios.get(`${API_URL}/groups`)
            ]);
            
            setAttendance(attendanceRes.data);
            setGroups(groupsRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to load timecard data');
        } finally {
            setLoading(false);
        }
    };

    const getGroupName = (groupId) => {
        if (!groupId) return 'Unassigned';
        const group = groups.find(g => g.id === groupId);
        return group?.name || 'Unknown';
    };

    const formatDate = (isoString) => {
        if (!isoString) return '-';
        return new Date(isoString).toLocaleDateString('en-AU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatTime = (isoString) => {
        if (!isoString) return '-';
        return new Date(isoString).toLocaleTimeString('en-AU', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDuration = (hours) => {
        if (!hours) return '-';
        const h = Math.floor(hours);
        const m = Math.round((hours - h) * 60);
        return `${h}h ${m}m`;
    };

    // Filter attendance based on search
    const filteredAttendance = attendance.filter(record => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
            record.first_name?.toLowerCase().includes(searchLower) ||
            record.last_name?.toLowerCase().includes(searchLower) ||
            record.username?.toLowerCase().includes(searchLower) ||
            record.activity_name?.toLowerCase().includes(searchLower)
        );
    });

    // Sort by date descending, then by clock_in time
    const sortedAttendance = [...filteredAttendance].sort((a, b) => {
        return new Date(b.clock_in) - new Date(a.clock_in);
    });

    // Calculate summary stats
    const summaryStats = {
        totalRecords: filteredAttendance.length,
        totalHours: filteredAttendance.reduce((sum, r) => sum + (r.duration_hours || 0), 0),
        dutyOfficerShifts: filteredAttendance.filter(r => r.is_duty_officer).length,
        uniqueMembers: new Set(filteredAttendance.map(r => r.user_id)).size
    };

    // Generate month options (past 12 months)
    const monthOptions = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const label = date.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
        monthOptions.push({ value, label });
    }

    // CSV Download function
    const downloadCSV = () => {
        // Prepare CSV data
        const headers = ['SES ID', 'First Name', 'Surname', 'Activity', 'Date', 'Time In', 'Time Out', 'Duration (hrs)', 'Duty Officer', 'Group'];
        
        const rows = sortedAttendance.map(record => {
            return [
                record.username || '-',
                record.first_name || '-',
                record.last_name || '-',
                record.activity_name || 'General Attendance',
                formatDate(record.clock_in),
                formatTime(record.clock_in),
                record.clock_out ? formatTime(record.clock_out) : 'Active',
                record.duration_hours ? record.duration_hours.toFixed(2) : '-',
                record.is_duty_officer ? 'Yes' : 'No',
                record.group_id ? getGroupName(record.group_id) : '-'
            ];
        });

        // Build CSV content
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        const [year, month] = selectedMonth.split('-');
        const monthName = new Date(year, month - 1).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
        const groupName = selectedGroup === 'all' ? 'AllGroups' : getGroupName(selectedGroup).replace(/\s+/g, '');
        
        link.setAttribute('href', url);
        link.setAttribute('download', `Timecard_${groupName}_${monthName.replace(/\s+/g, '_')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success('CSV downloaded successfully');
    };

    // Get available groups for filter (restricted for group-level users)
    const availableGroups = isGroupLevelOnly() 
        ? groups.filter(g => g.id === user?.group_id)
        : groups;

    if (!hasGroupAccess()) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <Card className="max-w-md w-full">
                    <CardContent className="p-6 text-center">
                        <Clock className="w-12 h-12 mx-auto mb-4 text-destructive" />
                        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
                        <p className="text-muted-foreground mb-4">
                            You don't have permission to view timecards.
                        </p>
                        <Button onClick={() => navigate('/dashboard')}>
                            Return to Dashboard
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="timecard-page">
            {/* Header */}
            <header className="bg-primary text-white sticky top-0 z-30">
                <div className="flex items-center justify-between h-14 px-4">
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate('/dashboard')}
                            className="text-white hover:bg-white/20"
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <h1 className="text-lg font-semibold">Timecard</h1>
                    </div>
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={downloadCSV}
                        disabled={sortedAttendance.length === 0}
                        data-testid="export-csv-btn"
                    >
                        <FileSpreadsheet className="w-4 h-4 mr-1" />
                        Download CSV
                    </Button>
                </div>
            </header>

            <div className="p-4 space-y-4">
                {/* Filters */}
                <Card>
                    <CardContent className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            {/* Group Filter - Hidden or restricted for group-level users */}
                            {!isGroupLevelOnly() ? (
                                <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                                    <SelectTrigger data-testid="filter-group">
                                        <Filter className="w-4 h-4 mr-2" />
                                        <SelectValue placeholder="All Groups" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Groups</SelectItem>
                                        {availableGroups.map((group) => (
                                            <SelectItem key={group.id} value={group.id}>
                                                {group.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-md">
                                    <Users className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-sm font-medium">{getGroupName(user?.group_id)}</span>
                                </div>
                            )}
                            
                            {/* Month Filter */}
                            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                                <SelectTrigger data-testid="filter-month">
                                    <Calendar className="w-4 h-4 mr-2" />
                                    <SelectValue placeholder="Select Month" />
                                </SelectTrigger>
                                <SelectContent>
                                    {monthOptions.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            
                            {/* Search */}
                            <div className="relative md:col-span-2">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search by name or activity..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="pl-10"
                                    data-testid="search-timecard"
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-primary">
                                {summaryStats.totalRecords}
                            </p>
                            <p className="text-xs text-muted-foreground">Total Records</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-primary">
                                {Math.round(summaryStats.totalHours)}
                            </p>
                            <p className="text-xs text-muted-foreground">Total Hours</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-purple-600">
                                {summaryStats.dutyOfficerShifts}
                            </p>
                            <p className="text-xs text-muted-foreground">DO Shifts</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-primary">
                                {summaryStats.uniqueMembers}
                            </p>
                            <p className="text-xs text-muted-foreground">Active Members</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Timecard Table */}
                <Card>
                    <CardHeader className="py-3 px-4 bg-muted/30">
                        <CardTitle className="text-sm font-medium flex items-center justify-between">
                            <span className="flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                Attendance Records
                            </span>
                            <Badge variant="secondary">
                                {sortedAttendance.length} records
                            </Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="p-8 text-center">
                                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
                                <p className="text-muted-foreground mt-2">Loading...</p>
                            </div>
                        ) : sortedAttendance.length === 0 ? (
                            <div className="p-8 text-center">
                                <Clock className="w-12 h-12 mx-auto mb-2 text-muted-foreground opacity-50" />
                                <p className="text-muted-foreground">No attendance records found</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted/50">
                                        <tr className="border-b">
                                            <th className="p-3 text-left font-medium text-muted-foreground">SES ID</th>
                                            <th className="p-3 text-left font-medium text-muted-foreground">First Name</th>
                                            <th className="p-3 text-left font-medium text-muted-foreground">Surname</th>
                                            <th className="p-3 text-left font-medium text-muted-foreground">Activity</th>
                                            <th className="p-3 text-left font-medium text-muted-foreground">Date</th>
                                            <th className="p-3 text-left font-medium text-muted-foreground">Time In</th>
                                            <th className="p-3 text-left font-medium text-muted-foreground">Time Out</th>
                                            <th className="p-3 text-left font-medium text-muted-foreground">Duration</th>
                                            <th className="p-3 text-left font-medium text-muted-foreground">DO</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedAttendance.map((record) => {
                                            return (
                                                <tr 
                                                    key={record.id} 
                                                    className="border-b hover:bg-muted/30 transition-colors"
                                                    data-testid={`record-${record.id}`}
                                                >
                                                    <td className="p-3 font-mono text-xs">
                                                        {record.username || '-'}
                                                    </td>
                                                    <td className="p-3">
                                                        {record.first_name || '-'}
                                                    </td>
                                                    <td className="p-3">
                                                        {record.last_name || '-'}
                                                    </td>
                                                    <td className="p-3">
                                                        <span className="truncate max-w-[150px] inline-block">
                                                            {record.activity_name || 'General Attendance'}
                                                        </span>
                                                    </td>
                                                    <td className="p-3 whitespace-nowrap">
                                                        {formatDate(record.clock_in)}
                                                    </td>
                                                    <td className="p-3 whitespace-nowrap">
                                                        {formatTime(record.clock_in)}
                                                    </td>
                                                    <td className="p-3 whitespace-nowrap">
                                                        {record.clock_out ? (
                                                            formatTime(record.clock_out)
                                                        ) : (
                                                            <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                                                                Active
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td className="p-3 whitespace-nowrap font-medium text-primary">
                                                        {formatDuration(record.duration_hours)}
                                                    </td>
                                                    <td className="p-3">
                                                        {record.is_duty_officer && (
                                                            <Badge variant="secondary" className="bg-purple-100 text-purple-700 text-xs">
                                                                <Shield className="w-3 h-3" />
                                                            </Badge>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
